import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { Form, FieldMultiSelect } from '../../../components';
import css from './ShopSettingsForm.module.css';
import { useIntl } from 'react-intl';
import CoverPhotoSection from './CoverPhotoSection';
import ProfilePhotoSection from './ProfilePhotoSection';
import BioSection from './BioSection';
import { useDispatch, useSelector } from 'react-redux';
import StatsSections from './StatsSections';
import ReviewRatingSection from './ReviewRatingSection';
import UserNameSection from './UserNameSection';
import ListingsTable from './ListingsTable';
import ShopSearchBar from './ShopSearchBar';
import { useConfiguration } from '../../../context/configurationContext';
import { updateProfileInProgressSelector } from '../../ProfileSettingsPage/ProfileSettingsPage.duck';
import { ListingState } from '../../../util/enums';
import OfferToggle from './OfferToggle/OfferToggle';
import getCountryCodes from '../../../translations/countryCodes';
import WalletToggle from './WalletToggle/WalletToggle';

// Normalize to a stable primitive key for comparison (string, e.g. "BE")
const getCountryKey = countryValue => {
  if (!countryValue) return null;

  // react-select style: array of options
  if (Array.isArray(countryValue)) {
    const first = countryValue[0];
    if (!first) return null;
    countryValue = first;
  }

  if (typeof countryValue === 'object') {
    // Prefer .value, then .code, then .key
    return countryValue.value || countryValue.code || countryValue.key || null;
  }

  // Simple string or primitive
  return countryValue;
};

// Auto-save helper for country field: runs ONLY when the normalized key changes
const AutoSaveCountry = ({ countryValue, values, saveProfile }) => {
  const firstRunRef = useRef(true);
  const lastKeyRef = useRef(null);

  // Keep latest form values without triggering re-runs
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  // Keep latest saveProfile without putting it in the effect deps
  const saveProfileRef = useRef(saveProfile);
  useEffect(() => {
    saveProfileRef.current = saveProfile;
  }, [saveProfile]);

  const normalizedKey = useMemo(() => getCountryKey(countryValue), [countryValue]);

  useEffect(() => {
    // First render: just store the initial key, do nothing
    if (firstRunRef.current) {
      firstRunRef.current = false;
      lastKeyRef.current = normalizedKey;
      return;
    }

    // If key hasn't changed, do nothing
    if (normalizedKey === lastKeyRef.current) {
      return;
    }

    // Update last key and call saveProfile once per real change
    lastKeyRef.current = normalizedKey;

    if (saveProfileRef.current) {
      saveProfileRef.current(valuesRef.current);
    }
  }, [normalizedKey]);

  return null;
};

const ShopSettingsForm = props => {
  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId = 'ShopSettingsForm',
          handleSubmit,
          inProgress,
          invalid,
          values,
          profileCreatedAt,
          lastLogin,
          uploadImageInProgress,
          reviews,
          history,
          routeConfiguration,
          location,
          listings,
          listingsAreLoading,
          draftListings,
          publishedListings,
          closedListings,
          isMobileLayout,
          form,
          hasPaymentMethodConnected,
          shopifyConnected,
        } = fieldRenderProps;

        const intl = useIntl();
        const dispatch = useDispatch();
        const config = useConfiguration();
        const updateProfileInProgress = useSelector(updateProfileInProgressSelector);
        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;
        const [activeState, setActiveState] = useState(ListingState.PUBLISHED);

        const countryCodes = getCountryCodes(config.localization.locale);

        // Use page-level onSubmit as the saveProfile handler for auto-saving country
        const saveProfile = props.onSubmit;

        const commonProps = {
          intl,
          values,
          dispatch,
          config,
          uploadImageInProgress,
          history,
          routeConfiguration,
          location,
          listings,
          listingsAreLoading,
          form,
          isMobileLayout,
          updateProfileInProgress,
          formId,
        };

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.profileContainer}>
              {/* <Field name="coverPhoto" component={CoverPhotoSection} {...commonProps} /> */}
              <div className={css.profileInfo}>
                <Field name="profilePhoto" component={ProfilePhotoSection} {...commonProps} />
                <div className={css.userDetails}>
                  <UserNameSection {...commonProps} />

                  <ReviewRatingSection {...commonProps} reviews={reviews} />

                  <div className={css.userTopDetails}>
                    <Field intl={intl} name="bio" component={BioSection} {...commonProps} />
                    <StatsSections
                      intl={intl}
                      profileCreatedAt={profileCreatedAt}
                      lastLogin={lastLogin}
                    />
                  </div>

                  {/* Country selector (aligned with SignupForm) */}
                  <div className={css.countryRow}>
                    <Field
                      name="country"
                      label={intl.formatMessage({ id: 'ShopSettingsForm.country' })}
                      component={FieldMultiSelect}
                      options={countryCodes}
                      isMulti={false}
                    />
                  </div>
                </div>
              </div>

              <OfferToggle />
              <WalletToggle hasPaymentMethodConnected={hasPaymentMethodConnected} />

              <ListingsTable
                {...commonProps}
                draftListings={draftListings}
                publishedListings={publishedListings}
                closedListings={closedListings}
                ShopSearchBar={<ShopSearchBar {...commonProps} isMobileLayout={isMobileLayout} />}
                isMobileLayout={isMobileLayout}
                setActiveState={setActiveState}
                activeState={activeState}
                listingsAreLoading={listingsAreLoading}
                shopifyConnected={shopifyConnected}
              />
            </div>

            {/* Auto-save when country changes ONLY */}
            <AutoSaveCountry
              countryValue={values?.country}
              values={values}
              saveProfile={saveProfile}
            />

            {/* No explicit save button required for country */}
          </Form>
        );
      }}
    />
  );
};

export default ShopSettingsForm;
